import { Link } from "react-router-dom";
import { ArrowRight, Clock, BookOpen, MapPin } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { GUIDES, GUIDE_CATEGORIES } from "@/data/guides";
import { CITIES } from "@/data/cities";

export default function LearnPage() {
  return (
    <Layout>
      <Seo title="Portugal Knowledge Hub — Guides for Indians | Effizent Europe" description="Expert guides on moving to Portugal, the D2 visa, company formation, cost of living and city guides — the definitive resource for Indians building a future in Portugal." />
      <PageHero eyebrow="Knowledge Hub" title="Learn everything about Portugal." subtitle="The definitive, expert-written resource for Indians planning to build a future in Portugal — visas, business, living and cities." crumbs={[{ label: "Learn" }]} />

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-20">
        <div className="flex items-center gap-3 mb-8"><BookOpen className="h-6 w-6 text-gold" /><h2 className="font-playfair text-3xl text-navy">Guides & Articles</h2></div>
        <div className="grid gap-5 md:grid-cols-2">
          {GUIDES.map((g, i) => (
            <Reveal key={g.slug} delay={i * 0.05}>
              <Link to={`/guides/${g.slug}`} data-testid={`learn-guide-${g.slug}`} className="group flex flex-col h-full rounded-3xl bg-white border border-navy/10 p-8 hover:border-gold/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                <span className="accent-eyebrow text-terracotta">{GUIDE_CATEGORIES.find((c) => c.id === g.category)?.label}</span>
                <h3 className="font-playfair text-2xl text-navy mt-3 group-hover:text-gold transition-colors">{g.title}</h3>
                <p className="text-navy/60 mt-3 leading-relaxed flex-1">{g.excerpt}</p>
                <div className="mt-6 flex items-center justify-between text-xs text-navy/45">
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {g.readTime} · {g.updated}</span>
                  <ArrowRight className="h-4 w-4 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-20 mb-8"><MapPin className="h-6 w-6 text-gold" /><h2 className="font-playfair text-3xl text-navy">City Guides</h2></div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CITIES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.05}>
              <Link to={`/cities/${c.slug}`} data-testid={`learn-city-${c.slug}`} className="group block relative h-64 rounded-3xl overflow-hidden">
                <img src={c.image} alt={`${c.name}, Portugal`} className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/20 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-playfair text-2xl text-warm">{c.name}</h3>
                  <p className="text-warm/70 text-sm mt-1">{c.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </Layout>
  );
}
